import React, { useState } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Select, MenuItem, Stack, Typography, Box } from '@mui/material';
import { useLang } from '../../contexts/langContext';
import { generateImage } from '../../services/images';
import CircularProgress from '@mui/material/CircularProgress';
import { FluxImageGenerationRequest } from '../../types';
import { magicButtonProps } from '../common/ai';
import ProtectedImage from '../common/ProtectedImage';

interface ImageGenerationDialogProps {
  open: boolean;
  onClose: () => void;
  onGenerateSuccess: ({url, _id}: {url: string, _id: string}) => void;
}

function ImageGenerationDialog({ open, onClose, onGenerateSuccess }: ImageGenerationDialogProps) {
  const { lang } = useLang();
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [prompt, setPrompt] = useState('');
  const [imageSize, setImageSize] = useState<FluxImageGenerationRequest['image_size']>('landscape_4_3');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImageInfo, setGeneratedImageInfo] = useState<{url: string, _id: string} | null>(null);

  const handleCloseDialog = () => {
    onClose();
    // Reset all states when closing
    setCurrentStep(1);
    setIsGenerating(false);
    setPrompt('');
    setGeneratedImageInfo(null);
  };

  const handleGenerateImage = async () => {
    try {
      setIsGenerating(true);
      const result = await generateImage({ prompt, image_size: imageSize });
      setGeneratedImageInfo(result);
      setCurrentStep(2);
    } catch (error) {
      console.error('Error generating image:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRetry = () => {
    setCurrentStep(1);
    setGeneratedImageInfo(null);
    setIsGenerating(false);
  };

  const handleConfirm = () => {
    if (!generatedImageInfo) {
      return;
    }
    onGenerateSuccess(generatedImageInfo);
    handleCloseDialog();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && currentStep === 1) {
      event.preventDefault();
      handleGenerateImage();
    }
  };

  const getDialogTitle = () => {
    if (currentStep === 1) {
      return lang === 'en' ? 'Generate Image' : '生成图片';
    } else {
      return lang === 'en' ? 'Preview Generated Image' : '预览生成的图片';
    }
  };

  return (
    <Dialog open={open} onClose={handleCloseDialog} maxWidth="md" fullWidth>
      <DialogTitle>{getDialogTitle()}</DialogTitle>
      <DialogContent>
        {currentStep === 1 ? (
          <>
            <TextField
              autoFocus
              margin="dense"
              placeholder={lang === 'en' ? 'Describe the image you want to generate' : '描述你想要生成的图片'}
              type="text"
              fullWidth
              rows={2}
              sx={{ minWidth: [300, 560] }}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isGenerating}
            />
            <FormControl variant="standard" component="fieldset" sx={{ mt: 2 }} disabled={isGenerating}>
              <Stack direction="row" spacing={2} alignItems="center">
                <FormLabel component="legend">{lang === 'en' ? 'Aspect Ratio:' : '比例:'}</FormLabel>
                <Select
                  value={imageSize}
                  onChange={(e) => setImageSize(e.target.value as FluxImageGenerationRequest['image_size'])}
                  sx={{ minWidth: 120 }}
                >
                  <MenuItem value="square_hd">{lang === 'en' ? 'Square HD' : '高清方形'}</MenuItem>
                  <MenuItem value="square">{lang === 'en' ? 'Square' : '方形'}</MenuItem>
                  <MenuItem value="portrait_4_3">{lang === 'en' ? 'Portrait 4:3' : '竖向 4:3'}</MenuItem>
                  <MenuItem value="portrait_16_9">{lang === 'en' ? 'Portrait 16:9' : '竖向 16:9'}</MenuItem>
                  <MenuItem value="landscape_4_3">{lang === 'en' ? 'Landscape 4:3' : '横向 4:3'}</MenuItem>
                  <MenuItem value="landscape_16_9">{lang === 'en' ? 'Landscape 16:9' : '横向 16:9'}</MenuItem>
                </Select>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {lang === 'en' 
                  ? 'Note: Actual generated image size may vary due to the deployed text-to-image model. Not all models support all aspect ratios.'
                  : '提示：由于部署的文本转图像模型的限制，实际生成的图片尺寸可能会有所不同。并非所有模型都支持所有比例。'
                }
              </Typography>
            </FormControl>
          </>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {lang === 'en' ? 'Prompt:' : '提示词:'} {prompt}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {lang === 'en' ? 'Aspect Ratio:' : '比例:'} {imageSize}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              {generatedImageInfo && (
                <ProtectedImage
                  imageUrl={generatedImageInfo.url}
                  alt={prompt}
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '400px',
                    objectFit: 'contain',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                />
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialog}>
          {lang === 'en' ? 'Cancel' : '取消'}
        </Button>
        {currentStep === 1 ? (
          <Button
            onClick={handleGenerateImage}
            variant="contained"
            disabled={isGenerating || !prompt.trim()}
          >
            {isGenerating ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              lang === 'en' ? 'Generate' : '生成'
            )}
          </Button>
        ) : (
          <>
            <Button onClick={handleRetry}>
              {lang === 'en' ? 'Retry' : '重试'}
            </Button>
            <Button onClick={handleConfirm} variant="contained">
              {lang === 'en' ? 'Confirm' : '确认'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ImageGenerationDialog; 